import { AlertTriangle, DollarSign, FileCheck, Receipt, Scale, Wrench } from "lucide-react";
import { Section, SectionHeading, PageHero } from "../components/site/section";
import { PageCta } from "../components/site/page-cta";
import { asset } from "../lib/assets";

const logo311 = { url: asset("nyc-311.png") };
const logoDob = { url: asset("nyc-dob.png") };
const logoDdc = { url: asset("logo-ddc.png") };
const logoDof = { url: asset("nyc-dof.png") };
const logoDot = { url: asset("nyc-dot.png") };
const logoHpd = { url: asset("nyc-hpd.png") };
const logoLpc = { url: asset("nyc-lpc.png") };
const logoDoris = { url: asset("nyc-doris.png") };
const logoFdny = { url: asset("fdny.webp") };
const logoOath = { url: asset("nyc-oath.png") };
const logoDep = { url: asset("nyc-dep.jpg") };
const logoDcp = { url: asset("dcp.jpg") };

const logoAgencies = [
    { name: "NYC 311", src: logo311.url, logoClass: "scale-100" },
    { name: "NYC Department of Buildings", src: logoDob.url, logoClass: "scale-[1.62]" },
    { name: "NYC Department of Transportation", src: logoDot.url, logoClass: "scale-[1.73]" },
    { name: "NYC Department of Design and Construction", src: logoDdc.url, logoClass: "scale-[1.25]" },
    { name: "NYC Department of Finance", src: logoDof.url, logoClass: "scale-[1.8]" },
    { name: "NYC Department of Housing Preservation & Development", src: logoHpd.url, logoClass: "scale-[1.85]" },
    { name: "NYC Landmarks Preservation Commission", src: logoLpc.url, logoClass: "scale-[1.71]" },
    { name: "NYC Department of Records & Information Services", src: logoDoris.url, logoClass: "scale-[1.76]" },
    { name: "Fire Department of the City of New York", src: logoFdny.url, logoClass: "scale-[1.25]" },
    { name: "NYC Department of City Planning", src: logoDcp.url, logoClass: "scale-[1.25]" },
    { name: "NYC Department of Environmental Protection", src: logoDep.url, logoClass: "scale-[0.8]" },
    { name: "NYC Office of Administrative Trials and Hearings", src: logoOath.url, logoClass: "scale-[1.25]" },
];
const monitoringCategories = [
    {
        title: "Violations",
        icon: AlertTriangle,
        items: [
            { title: "311 Complaints", description: "Citizen complaints filed through NYC 311 that may trigger agency inspections or enforcement." },
            { title: "DOB Complaints", description: "Department of Buildings complaints tied to construction, alteration or building conditions." },
            { title: "DOB Violations (BIS / DOB Now)", description: "Legacy BIS to latest DOB Now violations still on file for a property, including outstanding penalties." },
            { title: "ECB Violations", description: "Environmental Control Board violations covering quality-of-life and city-code offenses." },
            { title: "Housing Violations", description: "HPD housing maintenance code violations, from leaks and heat to structural conditions." },
            { title: "Housing Litigations", description: "Active HPD litigation cases brought against owners for unresolved violations." },
            { title: "Sidewalk Violations", description: "DOT sidewalk condition violations and repair notices, including curb and flagstone issues." },
            { title: "DOT Liens", description: "Liens placed by the Department of Transportation for unpaid sidewalk or violation charges." },
            { title: "Housing Complaints", description: "Tenant complaints submitted to HPD that can lead to inspections or violations." },
            { title: "Orders to Repair/Vacate", description: "Emergency orders requiring immediate repair or temporary vacation of all or part of a building." },
            { title: "Stop Work Orders", description: "DOB orders that halt construction activity until the cited condition is corrected." },
            { title: "FDNY Notice of Violation", description: "Fire Department violation notices for life-safety and fire-code deficiencies." },
            { title: "FDNY Vio Orders", description: "FDNY violation orders outlining required corrective actions and deadlines." },
            { title: "FDNY OATH Hearings", description: "Fire Department matters scheduled for hearing at the Office of Administrative Trials and Hearings." },
            { title: "OATH/ECB Hearings", description: "Hearings scheduled with OATH for ECB and other agency-enforced violations." },
            { title: "FDNY Permits", description: "Fire permits and approvals issued or renewed by the FDNY." },
            { title: "CityPay Tickets", description: "Summonses and tickets payable through the CityPay portal across city agencies." },
            { title: "Landmark Violations", description: "Landmarks Preservation Commission violations for work performed without approval." },
            { title: "DEP ECB Violations", description: "Department of Environmental Protection violations adjudicated through the ECB process." },
        ],
    },
    {
        title: "Infrastructure",
        icon: Wrench,
        items: [
            { title: "Work Applications (BIS / DOB Now)", description: "DOB work permit applications submitted to begin construction or alteration work, from legacy BIS to the latest DOB Now filings." },
            { title: "Permit Issuances", description: "Newly issued permits across DOB and related agencies so you know when work can start." },
            { title: "Approved Permits", description: "Permits that have received final approval and are ready to be picked up or acted on." },
            { title: "Facade Filings", description: "Façade inspection, repair and filing records including Local Law 11 compliance." },
            { title: "Illuminated Signs", description: "Sign permit records for illuminated and projecting signs, including renewals and violations." },
            { title: "Boilers Safety (DOB Now)", description: "Boiler compliance and safety filings tracked through DOB NOW." },
            { title: "Boilers (BIS)", description: "Legacy BIS boiler device records, inspections and compliance status." },
            { title: "Electrical Permit Applications (DOB Now)", description: "Electrical work permit applications filed in the DOB NOW portal." },
            { title: "Elevators Permit Application (DOB Now)", description: "Elevator permit applications submitted through DOB NOW." },
            { title: "Elevator Records (BIS)", description: "Legacy BIS elevator device records, inspections and outstanding conditions." },
            { title: "Limited Alteration Application (DOB Now)", description: "LAA filings for limited plumbing, mechanical and minor alteration work." },
            { title: "Landmark Buildings", description: "LPC-designated individual landmarks and historic district properties in your portfolio." },
            { title: "Landmark Applications", description: "Permits and applications involving landmark properties, including LPC approvals." },
        ],
    },
    {
        title: "Certifications",
        icon: FileCheck,
        items: [
            { title: "Certificates of Occupancy", description: "Legal certificates of occupancy confirming the approved use of each building or unit." },
            { title: "Board of Standards and Appeals", description: "BSA variances, special permits, appeals and modification decisions." },
            { title: "Tax Lien Sales", description: "Properties flagged for active or upcoming city tax lien sale eligibility." },
            { title: "ACRIS Legals", description: "Recorded legal documents from ACRIS, including mortgages, restrictions and UCC filings." },
            { title: "ACRIS Documents", description: "All recorded property documents from the Automated City Register Information System." },
            { title: "ACRIS Sales and Transfers", description: "Deed transfers, sale records and real-property transfer tax filings." },
            { title: "DOB Place of Assembly", description: "Place of Assembly certificates of operation and related compliance records." },
            { title: "SLA Liquor Licenses", description: "State Liquor Authority license records, including premises and license class details." },
            { title: "Deed Restrictions", description: "Recorded restrictive covenants, declarations and other deed-based limitations." },
        ],
    },
];
function PropertyData() {
    return (<>
      <PageHero title="Coverage across every office that can cite your building." description="Violerts serves as a central hub for navigating NYC’s complex network of property and building agencies. By bringing data from multiple agency databases into one streamlined platform, Violerts gives you a clear, unified view of the property information that matters—without the need to search across multiple systems."/>
      <Section>
        <SectionHeading title="Connected Agencies" align="center"/>
        <ul className="mx-auto mt-16 grid max-w-5xl grid-cols-2 gap-6 md:grid-cols-4">
          {logoAgencies.map((a) => (<li key={a.name} className="group">
              <div className="relative flex aspect-square flex-col items-center justify-center overflow-hidden rounded-[2rem] border border-border bg-card p-2 shadow-sm transition-all duration-300 group-hover:-translate-y-2 group-hover:border-signal/50 group-hover:shadow-lift">
                <img src={a.src} alt={a.name} className={`${a.logoClass} h-36 shrink-0 object-contain transition-opacity duration-300 group-hover:opacity-0`} loading="lazy"/>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-signal p-6 text-center opacity-0 transition-all duration-300 group-hover:opacity-100">
                  <span className="translate-y-2 text-sm font-bold leading-snug text-ink transition-transform duration-300 group-hover:translate-y-0">
                    {a.name}
                  </span>
                </div>
              </div>
            </li>))}
        </ul>
      </Section>

      <Section>
        <SectionHeading title="Everything Violerts monitors" description="Violerts watches the records that matter to NYC property owners and managers — violations, infrastructure filings and certifications — across the agencies that govern your buildings." align="center"/>
        <div className="mt-14 space-y-16">
          {monitoringCategories.map((cat) => (<div key={cat.title}>
              <div className="flex items-center gap-3">
                <cat.icon className="h-6 w-6 text-foreground"/>
                <h3 className="text-2xl font-bold md:text-3xl">{cat.title}</h3>
              </div>
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {cat.items.map((item) => (<div key={item.title} className="rounded-xl border border-border bg-card p-5 shadow-card transition-colors hover:border-signal/20">
                    <h4 className="text-base font-semibold">{item.title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                  </div>))}
              </div>
            </div>))}
        </div>
      </Section>

      <Section className="surface-ink">
        <div className="mx-auto max-w-5xl text-center">
          <SectionHeading title="Every penalty. Every refund. One clear view." description="Violerts doesn't just track violations — it surfaces the financial impact tied to each property. From outstanding DOB and ECB fines to refund eligibility and payment status, see exactly where money is owed, where it was recovered, and what still needs action." align="center" onInk/>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {[
            {
                icon: DollarSign,
                title: "Consolidated penalties",
                body: "Aggregate fines, fees, and accrued interest from DOB, ECB, HPD, and other agencies in a single running ledger.",
            },
            {
                icon: Receipt,
                title: "Refund tracking",
                body: "Monitor dismissed violations, overpayments, credits, and refunded amounts alongside their source records.",
            },
            {
                icon: Scale,
                title: "Tied to the record",
                body: "Every penalty and refund is linked to the underlying violation, hearing, or permit so nothing exists in a vacuum.",
            },
        ].map((item) => (<div key={item.title} className="rounded-2xl border border-border bg-card p-6 text-left shadow-card transition-colors hover:border-signal/30">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-foreground/10 text-foreground">
                  <item.icon className="h-5 w-5"/>
                </div>
                <h3 className="mt-4 font-semibold text-black">{item.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.body}</p>
              </div>))}
          </div>
          <p className="mx-auto mt-12 max-w-2xl text-sm leading-relaxed opacity-80">
            Property managers use this view to prioritize payments, dispute stale charges, and close
            out compliance issues faster — with full financial context attached.
          </p>
        </div>
      </Section>
      <PageCta />
    </>);
}

export default PropertyData;
